import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { GRACKLE_COLORS } from '@/lib/theme'

export type SheetColKind = 'text' | 'select' | 'date' | 'longtext' | 'readonly'

export interface SheetColumn {
  key: string
  label: string
  kind: SheetColKind
  /** Options for a select. Resolved by the page, so vocabularies stay server-driven. */
  options?: string[]
  width?: string
  /** A qualification the source header carried — kept rather than dropped. */
  note?: string
}

export interface SheetTableProps<T> {
  rows: T[]
  columns: SheetColumn[]
  /** Stable row key. */
  rowId: (row: T) => string
  /** Which brand colour tints this row, if any. */
  tint?: (row: T) => string | undefined
  /**
   * What to call this row in a control's accessible name.
   *
   * Without it every "Status" select on the page announces as just "Status", and a
   * screen-reader user cannot tell which client they are about to change. Usually
   * the client name.
   */
  rowLabel?: (row: T) => string
  /** Whether a given cell is editable by the current user. */
  canEdit?: (row: T, key: string) => boolean
  onSave?: (row: T, key: string, value: unknown) => void
  isLoading?: boolean
  /**
   * What to show instead of the table. A node, not a string, because an empty list is
   * often the result of a filter and needs to offer the way out of it — Advanced Demo
   * is 25 of 25 completed, so its default view is empty and needs a button, not an
   * apology.
   */
  emptyMessage?: React.ReactNode
  /** Screen-reader description of the table. */
  caption: string
}

/**
 * The spreadsheet-shaped table used by every Training screen.
 *
 * Extracted from the training worklist rather than copied into each of the six
 * pages. The workbook's tabs have between 4 and 24 columns and different
 * vocabularies, but identical mechanics: a wide scrollable grid, dropdowns where the
 * source had dropdowns, and inline editing gated by role.
 *
 * Two behaviours here are deliberate and were learned the hard way:
 *
 *   * 'N-A' is stored but 'N/A' is displayed. A slash in a stored value is a
 *     nuisance in URLs and CSV round-trips.
 *   * A cleared select sends null, not an empty string. Blank means "nobody has
 *     said", which several of these columns need to be able to express — the source
 *     uses it heavily and flattening it to a default is how information was lost
 *     the first time round.
 */
export function SheetTable<T>({
  rows,
  columns,
  rowId,
  tint,
  rowLabel,
  canEdit,
  onSave,
  isLoading,
  emptyMessage = 'Nothing to show.',
  caption,
}: SheetTableProps<T>) {
  const display = (v: unknown) => (v === 'N-A' ? 'N/A' : String(v ?? ''))

  const renderCell = (row: T, c: SheetColumn) => {
    const raw = (row as unknown as Record<string, unknown>)[c.key]
    const editable = c.kind !== 'readonly' && (canEdit?.(row, c.key) ?? false) && !!onSave
    const who = rowLabel?.(row)
    const label = who ? `${c.label} for ${who}` : c.label

    if (!editable) {
      if (c.key.endsWith('_url') && raw) {
        return (
          <a href={String(raw)} target="_blank" rel="noreferrer" className="underline">
            Link
          </a>
        )
      }
      return (
        <span className={c.kind === 'longtext' ? 'whitespace-pre-wrap' : ''}>
          {display(raw) || '—'}
        </span>
      )
    }

    if (c.kind === 'select') {
      // A Radix Select renders its placeholder when the value is not among its
      // items, which would silently hide what is stored. The sheet's own lists do
      // not cover all of its data — "Jeff" appears as a demo rep in four rows and in
      // no dropdown, and one onboarding row holds a variant spelling — so the stored
      // value is always offered, whether or not the vocabulary knows about it.
      const options = c.options ?? []
      const current = typeof raw === 'string' ? raw : ''
      const withCurrent = current && !options.includes(current) ? [...options, current] : options

      return (
        <Select
          value={current}
          onValueChange={(v) => onSave!(row, c.key, v === '__clear' ? null : v)}
        >
          <SelectTrigger className="h-8 w-full bg-white/70" aria-label={label}>
            <SelectValue placeholder="—" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__clear">—</SelectItem>
            {withCurrent.map((o) => (
              <SelectItem key={o} value={o}>
                {display(o)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )
    }

    if (c.kind === 'date') {
      return (
        <Input
          type="date"
          value={(raw as string) ?? ''}
          onChange={(e) => onSave!(row, c.key, e.target.value || null)}
          className="h-8 bg-white/70"
          aria-label={label}
        />
      )
    }

    return (
      <Input
        defaultValue={(raw as string) ?? ''}
        onBlur={(e) => {
          if (e.target.value !== ((raw as string) ?? '')) {
            onSave!(row, c.key, e.target.value || null)
          }
        }}
        className="h-8 bg-white/70"
        aria-label={label}
      />
    )
  }

  if (isLoading) {
    return (
      <p className="py-8 text-center text-muted-foreground" role="status">
        Loading...
      </p>
    )
  }
  if (rows.length === 0) {
    return <div className="py-8 text-center text-muted-foreground">{emptyMessage}</div>
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr style={{ backgroundColor: GRACKLE_COLORS.prussianBlue, color: '#FFFFFF' }}>
            {columns.map((c) => (
              <th
                key={c.key}
                scope="col"
                className={`p-2 text-left align-bottom text-xs font-semibold ${c.width ?? 'min-w-[10rem]'}`}
              >
                {c.label}
                {c.note && <span className="block font-normal opacity-80">({c.note})</span>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const bg = tint?.(row)
            return (
              <tr
                key={rowId(row)}
                className="border-b align-top"
                style={{
                  // Translucent, so the tint reads as a highlight rather than
                  // fighting the text — which stays Prussian Blue for contrast.
                  backgroundColor: bg ? `${bg}55` : undefined,
                  color: GRACKLE_COLORS.prussianBlue,
                }}
              >
                {columns.map((c) => (
                  <td key={c.key} className="p-1.5">
                    {renderCell(row, c)}
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/**
 * The control that brings finished rows back.
 *
 * Every Training list defaults to the outstanding work, because completed rows are the
 * bulk of the data and the most expensive thing to render — 177 of 249 sessions, and
 * each row mounts up to nine Radix Selects. So the finished work is never lost, only
 * put away, and this is the way back to it.
 *
 * The accessible name stays "Show completed" in both states, with `aria-pressed`
 * carrying the state. Accounts.tsx swaps its aria-label on toggle instead, which makes
 * a screen reader announce "Show all accounts, pressed" — a contradiction.
 */
export function ShowCompletedToggle({
  showCompleted,
  onToggle,
}: {
  showCompleted: boolean
  onToggle: () => void
}) {
  return (
    <Button
      variant={showCompleted ? 'default' : 'outline'}
      size="sm"
      onClick={onToggle}
      aria-pressed={showCompleted}
    >
      Show completed
    </Button>
  )
}

/**
 * The count of what is being kept out of the way, shown beside the row count.
 *
 * Renders nothing when nothing is hidden, so it never becomes noise on a list that has
 * no completed rows in it.
 */
export function CompletedHiddenNote({ hiddenCount }: { hiddenCount: number }) {
  if (hiddenCount < 1) return null
  return <span className="text-xs text-muted-foreground">{hiddenCount} completed hidden</span>
}

/**
 * The empty state when the default filter is the reason the list is empty.
 *
 * Worth its own component because it is the normal case on one tab: Advanced Demo is
 * 25 of 25 completed. No other list page in this app distinguishes "there is nothing"
 * from "you are not being shown it" — they all say "No X found. Create your first X" —
 * and on a page of finished history that reads as data loss.
 */
export function NothingOutstanding({
  hiddenCount,
  onShow,
}: {
  hiddenCount: number
  onShow: () => void
}) {
  return (
    <div className="flex flex-col items-center gap-2">
      <p className="font-medium text-foreground">Nothing outstanding.</p>
      <p className="text-sm">
        {hiddenCount === 1
          ? 'The only row here is completed and hidden.'
          : `All ${hiddenCount} rows here are completed and hidden.`}
      </p>
      <Button variant="outline" size="sm" onClick={onShow} className="mt-1">
        Show completed
      </Button>
    </div>
  )
}

/**
 * The status colour key shown under each table, so the tints are explained rather
 * than decorative.
 */
export function SheetColourKey({ tints }: { tints: Record<string, string> }) {
  return (
    <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
      <span className="font-medium">Row colour</span>
      {Object.entries(tints).map(([label, bg]) => (
        <span key={label} className="flex items-center gap-1.5">
          <span
            className="inline-block h-3 w-3 rounded-sm border border-black/20"
            style={{ backgroundColor: bg }}
            aria-hidden="true"
          />
          {label}
        </span>
      ))}
      <Badge variant="outline">Grackle brand palette</Badge>
    </div>
  )
}

/**
 * Shared page shell: heading, optional actions, search, then the table.
 */
export function SheetPage({
  title,
  description,
  icon,
  actions,
  toolbar,
  children,
}: {
  title: string
  description: string
  icon?: React.ReactNode
  actions?: React.ReactNode
  toolbar?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <main className="space-y-4 p-4 md:p-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
            {icon}
            {title}
          </h1>
          <p className="max-w-3xl text-sm text-muted-foreground">{description}</p>
        </div>
        {actions}
      </header>
      <Card>
        {toolbar && <CardHeader className="space-y-3">{toolbar}</CardHeader>}
        <CardContent className="p-0">{children}</CardContent>
      </Card>
    </main>
  )
}
